import { StyleSheet, Text, View } from "react-native";

import { Icon, type IconName } from "../Icon/Icon";
import { pictureScaleOf, scaled } from "../pictureScale";
import type { UiTheme } from "../theme";

// <MiniTrendBars> — a compact discrete bar-sparkline for a short series of
// weekly buckets (the "last 4 weeks" trend that rides next to a target row).
// Extracted from the hand-rolled bars in ProgramComposer/WeeklyTargetEditorRow
// when the 2nd callsite landed (PG-1 Progress muscle-coverage rows), so the
// editor's target-reference trend and the Progress coverage trend render the
// identical idiom from one place. NOT a LineChart: the datum is discrete weekly
// counts, i.e. bars, not a continuous line.
//
// Bars scale to the series' own max (each trend defines its own axis); the last
// (most-recent) bar is emphasised. Hidden from screen readers by default — the
// caller announces the same numbers in text (avg/last-week), so the bars are
// decorative reinforcement, never the only carrier.

export type MiniTrendBarsProps = {
  theme: UiTheme;
  // Weekly buckets, oldest → newest. Empty renders nothing.
  weeks: number[];
  // Track height in px (bars scale into this). Default 18 (the editor's size).
  height?: number;
  barWidth?: number;
  gap?: number;
  // Colour of the most-recent bar; earlier bars use restColor. Defaults keep
  // the editor's brand-emphasised-last idiom; the coverage row passes the
  // zone colour so the trend agrees with its target bar.
  lastColor?: string;
  restColor?: string;
  // Announce the bars to a screen reader? Off by default (the numbers live in
  // adjacent text) — matches the editor's accessibilityElementsHidden bars.
  accessibleBars?: boolean;
  // PG-17 / §8bis.1: the last bucket is the CURRENT week, still in progress.
  // It is drawn apart (a gap) and as an OUTLINE, so two days of a Tuesday
  // never read as a collapse next to three full weeks. The outline takes
  // `lastColor` when given, else a neutral ink: an in-progress week is not an
  // accent.
  partialLast?: boolean;
  // #336 slice 5, the rule card: an icon over a bar marks what kind of bucket
  // it is (never colour alone), the same icon the round shows elsewhere, and a
  // mono caption under each bar names it.
  marks?: ReadonlyArray<readonly MiniBarMark[] | undefined>;
  labels?: readonly string[];
};

/** What a bar can carry: an icon (a round type's glyph, «by hand»'s pencil). */
export type MiniBarMark = IconName;

const MARK_SIZE = 10;

// The in-progress week is a SHAPE marker, not a magnitude: on a Monday it holds
// almost nothing, and at the 2px floor of the other bars its outline vanished.
// It keeps its own floor so the marker is always there (clamped to the track).
export const PARTIAL_BAR_MIN_HEIGHT = 7;

// The outline of the in-progress week, in ONE place: these bars draw it, and
// so does charts/BarChart (PG-64 fetta 2).
export function partialBarStyle(theme: UiTheme, color?: string) {
  return {
    borderWidth: scaled(theme, 1),
    borderRadius: theme.radius.r1,
    backgroundColor: "transparent",
    borderColor: color ?? theme.palette.ink2,
  } as const;
}

// One bar step, shared with `charts/BarChart.tsx` (PG-63): each series scales
// to its own max, a bar never drops under a 2px floor, and the emphasised bar
// takes the brand while the rest stay quiet ink.
export const barScaleMax = (values: readonly number[]) => Math.max(1, ...values);

export const barHeight = (value: number, max: number, track: number) =>
  Math.max(2, Math.round((value / max) * track));

export const barFill = (
  theme: UiTheme,
  emphasised: boolean,
  colors?: { emphasis?: string; quiet?: string },
) =>
  emphasised
    ? (colors?.emphasis ?? theme.palette.brand500)
    : (colors?.quiet ?? theme.palette.ink3);

export function MiniTrendBars({
  theme,
  weeks,
  height: pointHeight = 18,
  barWidth: pointBarWidth = 6,
  gap: pointGap = 3,
  lastColor,
  restColor,
  accessibleBars = false,
  partialLast = false,
  marks,
  labels,
}: MiniTrendBarsProps) {
  const styles = makeStyles(theme);
  // A picture at k px per point draws the same bars, k times larger.
  const k = pictureScaleOf(theme);
  const height = pointHeight * k;
  const barWidth = pointBarWidth * k;
  const gap = pointGap * k;
  if (weeks.length === 0) return null;
  const max = barScaleMax(weeks);
  const a11y = accessibleBars
    ? {}
    : {
        accessibilityElementsHidden: true,
        importantForAccessibility: "no-hide-descendants" as const,
      };
  /** One bar: the in-progress week as an outline (partialLast), else filled.
   *  `apart`: the outline keeps its gap from the bars before it. */
  const bar = (w: number, idx: number, apart: boolean) => {
    const last = idx === weeks.length - 1;
    const h = barHeight(w, max, height);
    if (last && partialLast) {
      return (
        <View
          key={idx}
          testID="mini-trend-partial"
          style={[
            partialBarStyle(theme, lastColor),
            {
              width: barWidth,
              height: Math.min(height, Math.max(PARTIAL_BAR_MIN_HEIGHT * k, h)),
              ...(apart ? { marginLeft: gap } : {}),
            },
          ]}
        />
      );
    }
    return (
      <View
        key={idx}
        style={[
          styles.bar,
          {
            width: barWidth,
            height: h,
            backgroundColor: barFill(theme, last, { emphasis: lastColor, quiet: restColor }),
          },
        ]}
      />
    );
  };
  if (marks || labels) {
    // One column per bucket: its marks, its bar, its caption. The in-progress
    // week's column stands apart, as its bar does in the plain row.
    return (
      <View style={[styles.cols, { gap }]} {...a11y}>
        {weeks.map((w, idx) => (
          <View
            key={idx}
            style={[styles.col, partialLast && idx === weeks.length - 1 ? { marginLeft: gap } : null]}
          >
            <View style={[styles.marks, { height: MARK_SIZE * k }]}>
              {(marks?.[idx] ?? []).map((m) => (
                <Icon key={m} name={m} size={MARK_SIZE * k} color={theme.palette.ink2} />
              ))}
            </View>
            <View style={[styles.track, { height }]}>{bar(w, idx, false)}</View>
            {labels ? (
              <Text style={styles.label} numberOfLines={1}>
                {labels[idx] ?? ""}
              </Text>
            ) : null}
          </View>
        ))}
      </View>
    );
  }
  return (
    <View style={[styles.row, { height, gap }]} {...a11y}>
      {weeks.map((w, idx) => bar(w, idx, true))}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, radius: r, spacing: s, typography: t } = theme;
  return StyleSheet.create({
    row: { flexDirection: "row", alignItems: "flex-end" },
    bar: { borderRadius: r.r1 },
    cols: { flexDirection: "row", alignItems: "flex-end" },
    col: { flex: 1, alignItems: "center", gap: s.s1 },
    marks: { flexDirection: "row", alignItems: "center", gap: s.s1 },
    track: { justifyContent: "flex-end" },
    label: { color: p.ink2, fontSize: t.sizes.sm, fontFamily: t.fonts.mono },
  });
}
